import { useState } from "react";
import { IconPencil } from "@tabler/icons-react";
import Button from "../UI/Button";
import Card from "../UI/Card";
import IconButton from "../UI/IconButton";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import useRepartitionKeys from "../../hooks/use-repartition-keys";
import RepartitionKeyForm from "./RepartitionKeyForm";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import { BalanceDomain } from "../../utils/constants/balances";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import {
  RepartitionKeyUnit,
  repartitionKeyTotalShares,
} from "../../models/financeConfig";
import type {
  RepartitionKey,
  RepartitionKeyEntry,
} from "../../models/financeConfig";
import styles from "./RepartitionKeysShow.module.css";

interface RepartitionKeysShowProps {
  propertyFolderId: number;
}

export default function RepartitionKeysShow({
  propertyFolderId,
}: RepartitionKeysShowProps) {
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [keyToEdit, setKeyToEdit] = useState<RepartitionKey | null>(null);
  const { repartitionKeys, createKey, updateKey, deleteKey, isLoading } =
    useRepartitionKeys(propertyFolderId) as {
      repartitionKeys: RepartitionKey[];
      createKey: (values: Record<string, unknown>) => Promise<void>;
      updateKey: (id: number, values: Record<string, unknown>) => Promise<void>;
      deleteKey: (id: number) => Promise<void>;
      isLoading: boolean;
    };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };
  const { propertyFoldersById } = usePropertyFolders({ withById: true }) as {
    propertyFoldersById: Record<number, PropertyFolder>;
  };

  async function submitKeyHandler(values: Record<string, unknown>) {
    if (keyToEdit) {
      await updateKey(keyToEdit.id, values);
    } else {
      await createKey(values);
    }
    setKeyToEdit(null);
    setIsKeyModalOpen(false);
  }

  async function deleteKeyHandler() {
    if (!keyToEdit) return;
    await deleteKey(keyToEdit.id);
    setIsKeyModalOpen(false);
  }

  function repartitionLabel(repartition: RepartitionKeyEntry) {
    if (repartition.domaine === BalanceDomain.PROPERTY && repartition.id_bien) {
      return propertiesById[repartition.id_bien]?.nom ?? "N/A";
    }
    if (
      repartition.domaine === BalanceDomain.FOLDER &&
      repartition.id_property_folder
    ) {
      return propertyFoldersById[repartition.id_property_folder]?.nom ?? "N/A";
    }
    return "N/A";
  }

  function repartitionValue(
    key: RepartitionKey,
    repartition: RepartitionKeyEntry,
  ) {
    if (key.unit === RepartitionKeyUnit.SHARE) {
      return `${repartition.montant} / ${repartitionKeyTotalShares(
        key,
      )} tantièmes`;
    }
    return `${repartition.montant}%`;
  }

  return (
    <div className={styles.root}>
      <RepartitionKeyForm
        open={isKeyModalOpen}
        onOpenChange={(open) => {
          setIsKeyModalOpen(open);
          if (!open) setKeyToEdit(null);
        }}
        repartitionKey={keyToEdit}
        propertyFolderId={propertyFolderId}
        onSubmit={submitKeyHandler}
        onDelete={deleteKeyHandler}
      />
      <Card>
        <div className={styles.intro}>
          <div className={styles.introHeader}>
            <Title>Clés de répartitions</Title>
            <Button type="button" onClick={() => setIsKeyModalOpen(true)}>
              Ajouter une clé de répartition
            </Button>
          </div>
          <Text>
            Ce module sert à définir des clés de répartition personnalisées pour
            vos immeubles. Ces clés de répartitions pourrons être utilisées pour
            répartir une dépense entre les appartements.
          </Text>
          <Text>
            Par exemple, si vous fournissez internet à trois de vos locataires
            seulement, vous pouvez créer une clé de répartition "internet" qui
            répartira la dépense entre les appartements concernés.
          </Text>
        </div>
      </Card>

      {isLoading ? (
        <IsLoading />
      ) : repartitionKeys.length === 0 ? (
        <Card padding="md">
          <Text color="muted">Aucune clé de répartition définie</Text>
        </Card>
      ) : (
        <div className={styles.list}>
          {repartitionKeys.map((key) => (
            <Card key={key.id} padding="sm" className={styles.keyCard}>
              <div className={styles.keyHeader}>
                <Title
                  variant="kilo"
                  className={styles.keyTitle}
                  title={key.nom}
                >
                  {key.nom}
                </Title>
                <IconButton
                  type="button"
                  icon={IconPencil}
                  label="Modifier"
                  onClick={() => {
                    setKeyToEdit(key);
                    setIsKeyModalOpen(true);
                  }}
                />
              </div>
              <div>
                {key.repartition?.map((repartition, i) => (
                  <Text key={i} color="muted">
                    {repartitionLabel(repartition)} :{" "}
                    {repartitionValue(key, repartition)}
                  </Text>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
